import { useState, useEffect } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import { Ship, LogIn, ArrowRight, Menu, X } from 'lucide-react';
import { cn } from '@/utils';
import { useRouter } from 'next/router';

import { useScroll } from '../hooks/useScroll';

export function LandingTopBar({ onDemoClick }: { onDemoClick?: () => void }) {
  const scrolled = useScroll(50);
  const [mobileOpen, setMobileOpen] = useState(false);
  const router = useRouter();

  const links = [
    { label: 'Home', href: '/' },
    { label: 'About', href: '/about' },
    { label: 'Contact', href: '/contact' },
  ];

  return (
    <header className="sticky top-0 z-50">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className={cn(
          'relative mx-4 mt-4 rounded-2xl border px-4 py-3 backdrop-blur-xl transition-all duration-300 md:mx-8 md:px-6 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.3)]'
        )}
        style={{
          backgroundColor: scrolled
            ? 'rgba(15, 23, 42, 0.85)'
            : 'rgba(15, 23, 42, 0.4)',
          borderColor: scrolled
            ? 'rgba(255, 255, 255, 0.18)'
            : 'rgba(255, 255, 255, 0.12)',
        }}
      >
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center space-x-2.5">
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-white p-1 text-primary-foreground shadow-sm">
              <img src="/images/logo-missi.png" alt="Missi Track" width={36} height={36} className="h-full w-full object-contain" />
            </span>
            <span className="text-lg font-bold tracking-tight text-white transition-colors">
              Missi Track
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden items-center space-x-8 md:flex">
            {links.map((l) => (
              <Link
                key={l.label}
                href={l.href}
                className="text-sm font-medium text-slate-300 transition-colors hover:text-white"
              >
                {l.label}
              </Link>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden items-center space-x-2 md:flex">
            <Link
              href="/login"
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-brand-500/25 transition hover:bg-brand-600"
            >
              <LogIn className="h-4 w-4" />
              Login
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="rounded-lg p-2 text-white transition hover:bg-white/10 md:hidden"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {/* Mobile Menu Dropdown (Absolute Overlay — prevents video/hero layout push-down) */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.98 }}
              transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="absolute left-0 right-0 top-[calc(100%+0.75rem)] z-50 rounded-2xl border border-white/15 bg-slate-900/95 p-4 text-white backdrop-blur-2xl shadow-2xl md:hidden"
            >
              <nav className="flex flex-col gap-1">
                {links.map((l) => (
                  <Link
                    key={l.label}
                    href={l.href}
                    className="rounded-xl px-4 py-3 text-sm font-semibold text-white/90 transition hover:bg-white/10 hover:text-white"
                    onClick={() => setMobileOpen(false)}
                  >
                    {l.label}
                  </Link>
                ))}
                <div className="mt-3 border-t border-white/10 pt-3">
                  <Link
                    href="/login"
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-500 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-brand-500/25 transition hover:bg-brand-600"
                    onClick={() => setMobileOpen(false)}
                  >
                    <LogIn className="h-4 w-4" />
                    Login
                  </Link>
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </header>
  );
}

